import { type PetEquipment } from './petEquipmentService.ts';
import { mitigateDamage, isDamageType } from '../rules/damage.ts';
import type { PetCompanion } from '../domain/pet.ts';
import { petDamageResistance } from '../domain/pet.ts';
import { takePetPostMitigationDamage } from './petHealthService.ts';

export interface PetMovementOption {
  mode: 'ground' | 'swim' | 'burrow' | 'flight' | 'jump';
  feet: number | null;
  label: string;
}

export interface PetDamageMitigationResult {
  pet: PetCompanion;
  rawDamage: number;
  damageType: string;
  dr: number;
  steps: { label: string; running: number }[];
  afterDr: number;
  resisted: boolean;
  afterResistance: number;
  slotsLost: number;
  reducedToZero: boolean;
}

export function petMovementOptions(pet: Pick<PetCompanion, 'specialAbilities'>): PetMovementOption[] {
  const options: PetMovementOption[] = [
    pet.specialAbilities.includes('move-60')
      ? { mode: 'ground', feet: 60, label: 'Ground Move 60ft' }
      : { mode: 'ground', feet: null, label: 'Ground Move: standard pet value (20+S)' },
  ];
  if (pet.specialAbilities.includes('swim-30')) options.push({ mode: 'swim', feet: 30, label: 'Swim 30ft' });
  if (pet.specialAbilities.includes('burrow-30')) options.push({ mode: 'burrow', feet: 30, label: 'Burrow 30ft' });
  if (pet.specialAbilities.includes('flight-50')) options.push({ mode: 'flight', feet: 50, label: 'Flight 50ft' });
  if (pet.specialAbilities.includes('jump-50')) options.push({ mode: 'jump', feet: 50, label: 'Jump 50ft' });
  return options;
}

export function petHasHeightenedSenses(pet: Pick<PetCompanion, 'specialAbilities'>): boolean {
  return pet.specialAbilities.includes('heightened-senses');
}

export function petHasElementalResistance(
  pet: Pick<PetCompanion, 'specialAbilities' | 'resistanceDamageType'>,
  damageType: string,
): boolean {
  if (!pet.specialAbilities.includes('resistance-uncommon')) return false;
  const configured = pet.resistanceDamageType.trim().toLowerCase();
  return configured.length > 0 && configured === damageType.trim().toLowerCase();
}

/**
 * Applies printed mitigation order: DR first, then matching elemental
 * Resistance halves the remainder (rounded down), then Health Bar thresholds.
 */
export function takePetIncomingDamage(
  pet: PetCompanion,
  floor: number,
  rawDamage: number,
  damageType: string,
  now: string,
  equipment?: PetEquipment,
): PetDamageMitigationResult {
  if (!Number.isInteger(rawDamage) || rawDamage < 0) throw new Error('Incoming pet damage must be a whole number of at least 0.');
  const dr = petDamageResistance(pet, floor) + (equipment?.modifiers.damageResistance ?? 0);
  const afterDr = Math.max(0, rawDamage - dr);
  const typeName = damageType.trim().toLowerCase();
  const type = isDamageType(typeName) ? typeName : null;
  const innate = petHasElementalResistance(pet, damageType);
  const elementalResistances = [...(equipment?.modifiers.elementalResistances ?? []), ...(innate && type ? [type] : [])];
  const resisted = type !== null && elementalResistances.includes(type);
  const mitigated = mitigateDamage({ amount: rawDamage, type }, { damageResistance: dr, elementalResistances, vulnerabilities: equipment?.modifiers.vulnerabilities ?? [], immunities: equipment?.modifiers.immunities ?? [] });
  const afterResistance = mitigated.final;
  const health = takePetPostMitigationDamage(pet, afterResistance, now, equipment);
  return {
    pet: health.pet,
    rawDamage,
    damageType,
    dr,
    steps: mitigated.steps,
    afterDr,
    resisted,
    afterResistance,
    slotsLost: health.healthBar.slotsLost,
    reducedToZero: health.healthBar.reducedToZero,
  };
}
